// ============================================================
// SecaoFichaAtleta.js — a ficha do atleta
//
// Junta num lugar só o que hoje exige abrir quatro telas: o cadastro, a
// avaliação física, os testes e as competições. A pergunta que a ficha
// responde é "como esse atleta está", e ela não se responde olhando uma
// dessas coisas por vez — o corpo mudou, os testes melhoraram, e isso
// apareceu ou não na competição.
//
// ── O cuidado que define a tela ─────────────────────────────
//
// A ficha NÃO é classificação. Francisco Bruno e Ruan Rafael têm 0% de
// aproveitamento: disputaram cinco vezes a categoria Amador do Cearense de
// skate, contra o estado inteiro, e terminaram entre 6º e 18º. Isso não é
// fracasso, é um atleta de 19 anos competindo no nível acima do dele. Um
// "0%" grande ao lado da foto publica um juízo sobre a pessoa que o número
// não sustenta.
//
// Daí três decisões que o código mantém de propósito:
//
//   1. A ordem é contagem primeiro. Participações e pódios são fatos;
//      percentual é interpretação. O percentual entra pequeno e rotulado
//      como "pódios em participações", que é o que ele é.
//   2. Nenhum número da ficha é colorido por desempenho. Tudo no azul
//      institucional. Verde e vermelho dizem "bom" e "ruim", e não é a
//      ficha que decide isso, é o técnico, lendo o conjunto.
//   3. Nenhum atleta aparece comparado a outro aqui. O quadro de medalhas
//      da modalidade existe e é outra tela, feita para essa pergunta.
//
// ── Onde cada número é calculado ────────────────────────────
//
// Nenhum aqui. As contagens de competição vêm de `rankingDeMedalhas`, que
// roda `resumoDoAtleta` no servidor; a leitura da colocação vem de
// competicoes.js; a composição corporal vem de `compilarSerie`, a mesma
// função que o Excel e o PDF usam. Esta tela só escolhe o que mostrar.
// ============================================================

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  carregarMembrosGestao, carregarAvaliacoesFisicas, carregarAvaliacoesTecnicas,
  carregarTestesFisicos, carregarPresencasDoPeriodo, carregarDispensasDoPeriodo,
  rankingDeMedalhas, obterArquivoBiometrico, obterFotoDeCadastro,
} from "../firebase";
// A frequência é do programa, e não de uma modalidade: a mesma conta do
// relatório mensal, pela mesma função, para a ficha não inventar um
// segundo número para o mesmo fato.
import { calcularFrequencia, normalizarModalidade, normNome } from "../calculo";
import { compilarSerie } from "../avaliacaoFisica";
// As duas avaliações novas entram aqui como LEITURA. O lançamento é nas
// abas delas; esta tela é a que reúne tudo do atleta num lugar só, e uma
// ficha que mostra a antropometria e cala sobre a nota técnica deixa
// quem lê sem saber se o atleta não foi avaliado ou se a tela não traz.
import { compilarSerieTecnica, laudoTecnico } from "../avaliacaoTecnica";
import { compilarSerieDeTestes, bateriaDa } from "../testesFisicos";
import { serieDeEscores, somarFrequencia, frequenciaComEscore } from "../desempenho";
import { idadeDecimalEm } from "../idade";
import {
  historicoComEvolucao, historicoDoAtleta, rotuloDoRanking,
  rotuloDaEvolucao, rotuloDasPosicoes,
} from "../rankings";
import {
  gerarPDFAvaliacaoFisica, gerarPDFFichaCompleta,
  gerarPDFAvaliacaoTecnica, gerarPDFTestesFisicos,
} from "../pdfRelatorio";
import { agruparPorEvento, melhorDoAno, medalhaDe, rotuloDaColocacao } from "../competicoes";
import { idadeEm, ehMaiorDeIdade } from "../idade";
import {
  MESES, TEXTO, CINZA_ESC, BRANCO, BORDA, FUNDO_SUAVE, RAIO, ESP, TIPO, AZUL, OK, PERIGO,
} from "../tema";
import {
  GraficoDeMedida, INDICADORES, TrocaDeProtocolo,
  Card, Btn, Sel, StatusBox, Carregando, Vazio, Etiqueta,
  Estatistica, FileiraDeEstatisticas, CampoDeBusca, Avatar,
  EscoreGlobal, LegendaDosNiveis,
} from "../componentes";
import { useNomeNucleo } from "../NucleosContexto";
import useIsMobile from "../useIsMobile";

// ── Formatação ──────────────────────────────────────────────

function emBR(iso) {
  if (!iso) return "—";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso);
}

// Número com vírgula, que é como se escreve em português. `toFixed` devolve
// ponto, e "15.8%" ao lado de "66,2%" na mesma tela é descuido visível.
function num(v, casas = 1) {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return v.toFixed(casas).replace(".", ",");
}

// O aproveitamento impresso.
//
// Traço, e não "0%", quando não houve prova: quem não competiu não teve
// aproveitamento ruim, não teve aproveitamento. O servidor já devolve null
// nesse caso, e a tela precisa preservar a diferença.
function pct(v) {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  // Percentual redondo sai sem casa decimal: "100,0%" faz o olho parar na
  // casa em vez de no número, e a casa não está dizendo nada ali.
  return Number.isInteger(v) ? `${v}%` : `${num(v, 1)}%`;
}

function comSinal(v, unidade) {
  if (v === null || v === undefined || !Number.isFinite(v)) return null;
  const sinal = v > 0 ? "+" : "";
  return `${sinal}${num(v, 1)} ${unidade}`;
}

// ── Peças de leitura ────────────────────────────────────────

// Par rótulo/valor do bloco de cadastro. Em grade, para o olho descer pela
// coluna de rótulos em vez de ler frases.
function Dado({ rotulo, valor, apoio }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...TIPO.rotulo, color: CINZA_ESC }}>{rotulo}</div>
      <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 600, marginTop: 3 }}>
        {valor || "—"}
      </div>
      {apoio && (
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 1 }}>{apoio}</div>
      )}
    </div>
  );
}

function GradeDeDados({ children, minimo = 170 }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${minimo}px, 1fr))`,
      gap: ESP.lg,
    }}>
      {children}
    </div>
  );
}

// ── A tela ──────────────────────────────────────────────────

export default function SecaoFichaAtleta({ userNucleo }) {
  const nucleo = userNucleo || "";
  const nomeDoNucleo = useNomeNucleo();
  const isMobile = useIsMobile();

  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [pessoas, setPessoas] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [tecnicas, setTecnicas] = useState([]);
  const [testes, setTestes] = useState([]);
  const [busca, setBusca] = useState("");
  const [modalidade, setModalidade] = useState("");
  const [selecionado, setSelecionado] = useState(null);

  // `carregarMembrosGestao` e não `carregarAtletas`: a ficha precisa da data
  // de nascimento, da categoria, do sexo e dos dados do responsável, e o
  // outro carregador devolve só o que a grade de presença usa.
  const recarregar = useCallback(async () => {
    if (!nucleo) return;
    setCarregando(true);
    try {
      const [membros, coletas, tec, tst] = await Promise.all([
        carregarMembrosGestao(nucleo),
        carregarAvaliacoesFisicas(nucleo),
        carregarAvaliacoesTecnicas(nucleo),
        carregarTestesFisicos(nucleo),
      ]);
      setPessoas(membros.filter((m) => (m.perfil || "").toLowerCase().includes("atleta")));
      setAvaliacoes(coletas);
      setTecnicas(tec);
      setTestes(tst);
    } catch (e) {
      setStatus({ tipo: "erro", texto: `Não consegui carregar: ${e.message}` });
    } finally {
      setCarregando(false);
    }
  }, [nucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  const modalidades = useMemo(
    () => [...new Set(pessoas.map((p) => p.modalidade).filter(Boolean))].sort(),
    [pessoas],
  );

  // O desligado continua na lista, marcado.
  //
  // A ficha é histórico, não convocação: quem saiu do programa deixou
  // competições, medalhas e avaliações que continuam valendo e continuam
  // sendo consultadas. Esconder o nome esconderia o registro.
  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return pessoas
      .filter((p) => !modalidade || p.modalidade === modalidade)
      .filter((p) => !termo || (p.nome || "").toLowerCase().includes(termo))
      .sort((a, b) => {
        if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
        return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
      });
  }, [pessoas, busca, modalidade]);

  const coletasPorAtleta = useMemo(() => porAtletaUid(avaliacoes), [avaliacoes]);
  const tecnicasPorAtleta = useMemo(() => porAtletaUid(tecnicas), [tecnicas]);
  const testesPorAtleta = useMemo(() => porAtletaUid(testes), [testes]);

  if (!nucleo) return <Vazio titulo="Selecione um núcleo." />;
  if (carregando) return <Carregando linhas={5} />;

  const atleta = selecionado ? pessoas.find((p) => p.uid === selecionado) : null;

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      {status && <StatusBox tipo={status.tipo} msg={status.texto} />}

      {atleta ? (
        <Ficha
          atleta={atleta}
          coletas={coletasPorAtleta[atleta.uid] || []}
          tecnicas={tecnicasPorAtleta[atleta.uid] || []}
          testes={testesPorAtleta[atleta.uid] || []}
          nucleo={nucleo}
          nomeDoNucleo={nomeDoNucleo(nucleo)}
          isMobile={isMobile}
          onVoltar={() => setSelecionado(null)}
        />
      ) : (
        <Card
          titulo="Escolha o atleta"
          sub="A ficha reúne o cadastro, a avaliação física, os testes e as competições do atleta."
        >
          <div style={{ display: "flex", gap: ESP.md, flexWrap: "wrap", marginBottom: ESP.lg }}>
            <CampoDeBusca valor={busca} onChange={setBusca} placeholder="Buscar atleta" />
            <Sel
              value={modalidade} onChange={setModalidade}
              options={[
                { value: "", label: "Todas as modalidades" },
                ...modalidades.map((m) => ({ value: m, label: m })),
              ]}
            />
          </div>

          {lista.length === 0 ? (
            <Vazio titulo="Nenhum atleta neste recorte." />
          ) : (
            <div style={{ display: "grid", gap: ESP.sm }}>
              {lista.map((p) => {
                const coletas = coletasPorAtleta[p.uid] || [];
                const anos = idadeEm(p.dataNascimento);
                return (
                  <button
                    key={p.uid} onClick={() => setSelecionado(p.uid)}
                    style={{
                      display: "flex", alignItems: "center", gap: ESP.md,
                      width: "100%", textAlign: "left", cursor: "pointer",
                      background: BRANCO, border: `1px solid ${BORDA}`,
                      borderRadius: RAIO.md, padding: "10px 13px", font: "inherit",
                    }}
                  >
                    <Avatar nome={p.nome} tamanho={36} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", ...TIPO.corpo, fontWeight: 600, color: TEXTO }}>
                        {p.nome}
                      </span>
                      <span style={{ display: "block", ...TIPO.apoio, color: CINZA_ESC }}>
                        {[p.modalidade || "sem modalidade", p.categoria,
                          anos !== null && `${anos} anos`].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: ESP.sm, flexShrink: 0 }}>
                      {!p.ativo && <Etiqueta tom="neutro">Desligado</Etiqueta>}
                      <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                        {coletas.length
                          ? `${coletas.length} avaliaç${coletas.length > 1 ? "ões" : "ão"}`
                          : "sem avaliação"}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// Os documentos de uma coleção, agrupados por atleta.
function porAtletaUid(lista) {
  const mapa = {};
  (lista || []).forEach((x) => {
    if (!x || !x.atletaUid) return;
    (mapa[x.atletaUid] = mapa[x.atletaUid] || []).push(x);
  });
  return mapa;
}

// ── A ficha aberta ──────────────────────────────────────────

function Ficha({
  atleta, coletas, tecnicas = [], testes = [],
  nucleo, nomeDoNucleo, isMobile, onVoltar,
}) {
  // A ficha completa num PDF só: cadastro, avaliação física, técnica,
  // testes físicos e competições. É o documento da reunião de comissão
  // técnica e da prestação de contas, e a impressão isolada de cada área
  // continua na aba dela.
  //
  // A série é compilada aqui de novo, com a mesma função da tela, porque
  // o papel não pode sair de uma segunda conta: `compilarSerie` é pura,
  // e com a mesma entrada devolve o mesmo resultado.
  const [gerando, setGerando] = useState(false);
  const [erroNoPDF, setErroNoPDF] = useState(null);

  // O escore global das ondas do atleta, para o quadro da tela. O mesmo
  // cálculo vai junto na ficha completa, logo abaixo.
  const escores = useMemo(() => serieDeEscores({
    tecnica: compilarSerieTecnica({ modalidade: atleta.modalidade, avaliacoes: tecnicas }),
    fisica: compilarSerieDeTestes({
      modalidade: atleta.modalidade,
      idadeEm: (data) => idadeDecimalEm(atleta.dataNascimento, data),
      coletas: testes,
    }),
  }), [atleta.modalidade, atleta.dataNascimento, tecnicas, testes]);

  // A frequência mora aqui, e não dentro do bloco que a mostra, porque
  // ela vai junto na ficha completa em PDF. Ler duas vezes a mesma coisa
  // para a tela e para o papel é como tela e papel passam a discordar.
  const frequencia = useFrequenciaDoAtleta({ atleta, escores, nucleo });

  async function baixarFichaCompleta() {
    setGerando(true);
    setErroNoPDF(null);
    try {
      const serie = compilarSerie({
        sexo: atleta.sexo,
        dataNascimento: atleta.dataNascimento,
        avaliacoes: coletas,
      });
      const ultima = [...coletas].sort((a, b) => String(a.data).localeCompare(String(b.data))).pop();
      // O quadro da modalidade é pedido na hora, e não guardado: a
      // ficha completa é ação do usuário, e uma chamada a mais quando
      // ele clica custa menos que manter o dado carregado o tempo todo.
      let competicoes = null;
      try {
        const r = await rankingDeMedalhas({ nucleo, modalidade: atleta.modalidade });
        competicoes = {
          linha: (r.linhas || []).find((l) => l.uid === atleta.uid) || null,
          tamanhoDoPodio: r.tamanhoDoPodio,
        };
      } catch (e) { /* a ficha sai sem a seção de competições */ }

      // O retrato é buscado aqui, e não dentro do gerador: a leitura é
      // chamada autenticada e quem tem sessão é a tela. Mesma ordem do
      // cartão de cadastro, o retrato de uniforme antes da biométrica.
      // Falhar não cancela a ficha, só a deixa sem foto.
      let foto = null;
      try {
        foto = await obterFotoDeCadastro(atleta.uid);
      } catch (e) {
        try { foto = await obterArquivoBiometrico({ alunoUid: atleta.uid, qual: "foto" }); }
        catch (e2) { foto = null; }
      }
      if (!foto || !foto.base64) {
        try { foto = await obterArquivoBiometrico({ alunoUid: atleta.uid, qual: "foto" }); }
        catch (e) { foto = null; }
      }

      // As duas séries novas saem das mesmas funções que os blocos da
      // tela usam, e o escore do mesmo `serieDeEscores`. O papel não
      // pode sair de uma segunda conta.
      const serieTecnica = compilarSerieTecnica({
        modalidade: atleta.modalidade, avaliacoes: tecnicas,
      });
      const serieDeTestes = compilarSerieDeTestes({
        modalidade: atleta.modalidade,
        idadeEm: (data) => idadeDecimalEm(atleta.dataNascimento, data),
        coletas: testes,
      });

      await gerarPDFFichaCompleta({
        atleta, serie, sintese: (ultima && ultima.sintese) || null,
        serieTecnica, serieDeTestes, escores,
        // A mesma soma que o bloco da tela mostra. O papel não sai de uma
        // segunda conta.
        frequencia: frequencia.meses
          ? { periodo: frequencia.periodo, total: frequencia.total, porOnda: frequencia.porOnda }
          : null,
        competicoes, nucleo, nucleoNome: nomeDoNucleo, foto,
      });
    } catch (e) {
      setErroNoPDF(`Não consegui gerar a ficha: ${e.message}`);
    } finally {
      setGerando(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      <BlocoCadastro
        atleta={atleta} nomeDoNucleo={nomeDoNucleo}
        isMobile={isMobile} onVoltar={onVoltar}
        acao={(
          <Btn variant="secondary" tamanho="sm"
            onClick={baixarFichaCompleta} disabled={gerando}>
            {gerando ? "Gerando…" : "Imprimir ficha completa"}
          </Btn>
        )}
      />
      {erroNoPDF && <StatusBox tipo="erro" msg={erroNoPDF} />}
      <BlocoAvaliacaoFisica atleta={atleta} coletas={coletas} isMobile={isMobile}
        nucleo={nucleo} nomeDoNucleo={nomeDoNucleo} />
      <BlocoTecnica atleta={atleta} avaliacoes={tecnicas}
        nucleo={nucleo} nomeDoNucleo={nomeDoNucleo} />
      <BlocoTestes atleta={atleta} coletas={testes}
        nucleo={nucleo} nomeDoNucleo={nomeDoNucleo} />
      <BlocoEscore escores={escores} />
      {/* A frequência vem depois do escore porque é a leitura dele: o
          número do desempenho primeiro, e em seguida quanto treino houve
          até ele. Vale para as três modalidades. */}
      <BlocoFrequencia frequencia={frequencia} />
      <BlocoCompeticoes atleta={atleta} nucleo={nucleo} />
    </div>
  );
}

// O retrato, o nome e o essencial.
//
// ── Qual das duas fotos ─────────────────────────────────────
//
// O sistema guarda duas, e elas não servem à mesma coisa. A do CADASTRO é
// o retrato de uniforme, feito para documento e para esta ficha. A do
// cadastro FACIAL é a referência do reconhecimento na chamada, um rosto
// enquadrado para a máquina comparar.
//
// A ficha prefere a de cadastro, que é a que foi feita para ela, e cai na
// biométrica só quando aquela não existe. Enquanto o retrato de uniforme
// não estiver tirado para todo mundo, cair na biométrica mostra o atleta
// em vez de mostrar uma inicial.
//
// As duas vêm em bytes por chamada autenticada, e não por link: link de
// arquivo de pessoa sobrevive à sessão, fica no histórico do navegador, em
// print e em mensagem encaminhada. Aqui o acesso morre quando a aba fecha.
//
// Sem nenhuma das duas, a ficha abre com a inicial no círculo, igual às
// listagens, e isso não é lacuna que mereça aviso.
// ── Cadastro ────────────────────────────────────────────────

// ── Frequência ──────────────────────────────────────────────
//
// Duas perguntas numa só: quanto o atleta treinou, e quanto treino houve
// até cada avaliação. A segunda é o que liga frequência e desempenho sem
// inventar fórmula: os dois números na mesma linha, na mesma janela de
// tempo, e a leitura com quem treina.
//
// Vale para as três modalidades, porque a frequência é do programa e não
// da modalidade.
//
// ── Por que carrega aqui, e não na lista ────────────────────
//
// A consulta de presença do período inteiro é a maior desta tela. Quem
// está só procurando um nome na lista não deve pagá-la, e quem abre a
// ficha do segundo atleta não paga de novo: `carregarPresencasDoPeriodo`
// guarda a leitura por um minuto.
//
// ── Por que mês a mês ───────────────────────────────────────
//
// Porque o teto de justificativa é mensal. Calcular o ano de uma vez
// abonaria meio semestre de falta a quem faltou tudo num mês só, e o
// número da ficha deixaria de bater com o do relatório do mês.
// O gancho que carrega e soma a frequência do atleta.
//
// Separado do componente porque a ficha completa em PDF precisa do mesmo
// resultado, e porque assim o que ele devolve é exatamente o que vai para
// o papel.
function useFrequenciaDoAtleta({ atleta, escores, nucleo }) {
  const [meses, setMeses] = useState(null);
  const [erro, setErro] = useState(null);

  // O período: de janeiro do ano da primeira avaliação até hoje. Sem
  // avaliação nenhuma, o ano corrente, que é o que a coordenação olha.
  const periodo = useMemo(() => {
    const hoje = new Date();
    const datas = (escores || []).map((e) => e.data).filter(Boolean).sort();
    const anoInicial = datas.length ? Number(String(datas[0]).slice(0, 4)) : hoje.getFullYear();
    return {
      anoInicial,
      anoFinal: hoje.getFullYear(),
      mesFinal: hoje.getMonth(),
      de: `${anoInicial}-01-01`,
      ate: `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-31`,
      rotulo: `de janeiro de ${anoInicial} a ${MESES[hoje.getMonth()].toLowerCase()} `
        + `de ${hoje.getFullYear()}`,
    };
  }, [escores]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [registros, dispensas] = await Promise.all([
          carregarPresencasDoPeriodo(nucleo, periodo.de, periodo.ate),
          carregarDispensasDoPeriodo(nucleo, periodo.de, periodo.ate),
        ]);
        if (!vivo) return;
        setMeses(mesesDoAtleta({ atleta, registros, dispensas, periodo }));
      } catch (e) {
        if (vivo) setErro(e.message);
      }
    })();
    return () => { vivo = false; };
  }, [atleta, nucleo, periodo]);

  const total = useMemo(() => (meses ? somarFrequencia(meses) : null), [meses]);
  const porOnda = useMemo(
    () => (meses ? frequenciaComEscore({ meses, escores }) : []),
    [meses, escores],
  );

  return { meses, erro, periodo, total, porOnda };
}

function BlocoFrequencia({ frequencia }) {
  const { meses, erro, periodo, total, porOnda } = frequencia;

  if (erro) {
    return (
      <Card titulo="Frequência">
        <StatusBox tipo="erro" msg={`Não consegui ler a frequência: ${erro}`} />
      </Card>
    );
  }
  if (!meses) return <Card titulo="Frequência"><Carregando linhas={2} /></Card>;

  return (
    <Card
      titulo="Frequência"
      sub={`De janeiro de ${periodo.anoInicial} a ${MESES[periodo.mesFinal].toLowerCase()} de ${periodo.anoFinal}.`}
    >
      {!total.treinos ? (
        <Vazio icone="—" titulo="Nenhum treino no período para este atleta.">
          A frequência é apurada a partir dos registros de presença do núcleo, mês a mês,
          pelo mesmo cálculo do relatório mensal.
        </Vazio>
      ) : (
        <>
          <FileiraDeEstatisticas minimo={140}>
            <Estatistica rotulo="Treinos" valor={total.treinos} cor={AZUL}
              contexto="dias de treino no período" />
            <Estatistica rotulo="Presenças" valor={total.presencas} cor={AZUL}
              contexto="com presença registrada" />
            <Estatistica rotulo="Frequência absoluta" valor={pctFreq(total.pctAbs)} cor={AZUL}
              contexto="presenças sobre dias de treino" />
            <Estatistica rotulo="Frequência total" valor={pctFreq(total.pctTotal)} cor={AZUL}
              contexto="presenças e dias abonados" />
          </FileiraDeEstatisticas>

          <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6, marginTop: ESP.md }}>
            {`${total.abonos} dia${total.abonos === 1 ? "" : "s"} abonado${total.abonos === 1 ? "" : "s"}`}
            {` e ${total.faltas} falta${total.faltas === 1 ? "" : "s"}. `}
            A frequência absoluta considera apenas as presenças registradas em relação aos
            dias de treino do período. A frequência total acrescenta os dias abonados:
            competição, dispensa de modalidade, viagem e justificativa aceita pela
            coordenação.
          </div>

          {porOnda.length > 0 && (
            <div style={{ marginTop: ESP.lg, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}` }}>
              <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 600 }}>
                Frequência por período de avaliação
              </div>
              <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6, marginBottom: ESP.md }}>
                Cada linha corresponde ao período decorrido desde a avaliação anterior. Os
                dois indicadores são apresentados lado a lado, sem relação de cálculo entre
                si: a frequência não compõe o escore, e a interpretação conjunta cabe à
                comissão técnica.
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", ...TIPO.apoio }}>
                  <thead>
                    <tr>
                      {["Avaliação", "Período", "Treinos", "Absoluta", "Total", "Escore", "Variação"]
                        .map((c) => (
                          <th key={c} style={{
                            textAlign: "left", padding: "7px 9px", color: CINZA_ESC,
                            fontWeight: 600, borderBottom: `1px solid ${BORDA}`,
                            whiteSpace: "nowrap",
                          }}>{c}</th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {porOnda.map((l) => (
                      <tr key={l.data}>
                        <td style={TD_FREQ}>{emBRFicha(l.data)}</td>
                        <td style={{ ...TD_FREQ, color: CINZA_ESC }}>
                          {l.desdeAInicio ? "até esta data" : "desde a anterior"}
                        </td>
                        <td style={TD_FREQ}>{l.treinos}</td>
                        <td style={TD_FREQ}>{pctFreq(l.pctAbs)}</td>
                        <td style={TD_FREQ}>{pctFreq(l.pctTotal)}</td>
                        <td style={{ ...TD_FREQ, fontWeight: 700 }}>
                          {Number.isFinite(l.global) ? umaCasa(l.global, 1) : "—"}
                        </td>
                        <td style={{ ...TD_FREQ, color: CINZA_ESC }}>
                          {Number.isFinite(l.variacao)
                            ? `${l.variacao > 0 ? "+" : l.variacao < 0 ? "−" : ""}${umaCasa(Math.abs(l.variacao), 1)}`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

const TD_FREQ = { padding: "7px 9px", borderBottom: "1px solid #EEF1F5", whiteSpace: "nowrap" };

function pctFreq(v) {
  return Number.isFinite(v) ? `${umaCasa(v, 1)}%` : "—";
}

// Os meses do período, já calculados, só deste atleta.
//
// O casamento com a linha da frequência é por primeiro nome, último nome
// e modalidade, que é a MESMA chave que `calculo.js` usa para agrupar os
// registros. Casar por outra regra aqui faria a ficha discordar do
// relatório no dia em que alguém tiver nome composto.
function mesesDoAtleta({ atleta, registros, dispensas, periodo }) {
  const chaveDe = (nome, modalidade) => {
    const partes = normNome(nome || "").split(" ").filter(Boolean);
    const mod = normalizarModalidade(modalidade || "").toLowerCase();
    return `${partes[0] || ""}_${partes[partes.length - 1] || ""}_${mod}`;
  };
  const minha = chaveDe(atleta.nome, atleta.modalidade);

  const meses = [];
  for (let ano = periodo.anoInicial; ano <= periodo.anoFinal; ano += 1) {
    const ultimo = ano === periodo.anoFinal ? periodo.mesFinal : 11;
    for (let mes = 0; mes <= ultimo; mes += 1) {
      const doMes = (lista) => lista.filter((x) => {
        const iso = x.dateKey || x.data || "";
        const [a, m] = String(iso).split("-");
        return Number(a) === ano && Number(m) === mes + 1;
      });
      const calc = calcularFrequencia({
        registros: doMes(registros),
        dispensas: doMes(dispensas),
        mes,
        ano,
      });
      const eu = (calc.membros || []).find((x) => chaveDe(x.nome, x.modalidade) === minha);
      if (!eu) continue;
      meses.push({
        ano,
        mes,
        treinos: eu.treinos,
        presencas: eu.presEfetiva,
        // O abono é o que sobrou entre o treino, a presença e a falta.
        // Tirá-lo da subtração, e não de pctJust, evita arredondar duas
        // vezes o mesmo número.
        abonos: Math.max(0, eu.treinos - eu.presEfetiva - eu.faltas),
        faltas: eu.faltas,
      });
    }
  }
  return meses;
}

const ROTULO_DO_GENERO = { masculino: "Masculino", feminino: "Feminino" };
//
// O mesmo desenho do cartão de cadastro em Gestão: retrato à esquerda,
// informação em linhas rotuladas ao lado, situação à direita.
//
// Antes eram DOIS cartões para o mesmo atleta, um com a foto e o nome e
// outro com uma grade de campos logo abaixo, cada um num formato. O mesmo
// cadastro, lido em duas telas do portal, aparecia de dois jeitos, e quem
// conferia um contra o outro perdia tempo achando onde estava cada campo.
// Pôr a foto aqui sem juntar os dois deixaria o mesmo retrato duas vezes
// na mesma tela.
//
// A navegação e o botão de imprimir ficam no topo do cartão, porque são
// ações da ficha inteira e não do cadastro.
//
// Os campos são os que a ficha já tinha. O CPF continua de fora: ele
// existe para o termo de consentimento e não responde à pergunta que esta
// tela faz, que é "como esse atleta está".

function LinhaDeCadastro({ rotulo, valor, apoio = null, destaque = false }) {
  return (
    <div style={{ display: "flex", gap: 7, alignItems: "baseline", padding: "2px 0", flexWrap: "wrap" }}>
      <span style={{ ...TIPO.apoio, color: CINZA_ESC, fontWeight: 700 }}>{rotulo}:</span>
      <span style={{
        ...TIPO.corpo, color: valor ? TEXTO : CINZA_ESC,
        fontWeight: destaque ? 700 : 400,
      }}>
        {valor || "—"}
      </span>
      {apoio && <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>{apoio}</span>}
    </div>
  );
}

function BlocoCadastro({ atleta, nomeDoNucleo, isMobile, onVoltar, acao = null }) {
  const [foto, setFoto] = useState(null);
  const anos = idadeEm(atleta.dataNascimento);
  const maior = ehMaiorDeIdade(atleta.dataNascimento);
  const temResponsavel = Boolean(atleta.responsavelNome);

  useEffect(() => {
    let vivo = true;
    setFoto(null);
    const comoImagem = r => (r && r.base64 ? `data:${r.tipo};base64,${r.base64}` : null);

    // Atleta sem foto responde "not-found", e isso é a mesma informação
    // que a ausência: não há erro para mostrar. O recuo para a biométrica
    // precisa valer nos DOIS casos, o de recusa e o de resposta vazia.
    const tentarBiometrica = () =>
      obterArquivoBiometrico({ alunoUid: atleta.uid, qual: "foto" })
        .then(b => { if (vivo) setFoto(comoImagem(b)); })
        .catch(() => {});

    obterFotoDeCadastro(atleta.uid)
      .then(r => {
        const img = comoImagem(r);
        if (vivo && img) { setFoto(img); return null; }
        return tentarBiometrica();
      })
      .catch(tentarBiometrica);
    return () => { vivo = false; };
  }, [atleta.uid]);

  // O retrato é de uniforme, em pé: a mesma proporção da Gestão, para o
  // atleta ser reconhecido do mesmo jeito nas duas telas.
  const lado = isMobile ? 92 : 116;

  return (
    <Card titulo="Cadastro">
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, flexWrap: "wrap", marginBottom: ESP.md,
      }}>
        <Btn variant="discreto" tamanho="sm" onClick={onVoltar}>← Todos os atletas</Btn>
        {acao}
      </div>

      <div style={{ display: "flex", gap: ESP.lg, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* O retrato do desligado fica em preto e branco, como na Gestão.
            É a marca que se lê antes de qualquer texto, e diz "esteve no
            programa" sem ocupar uma linha para dizer isso. */}
        {foto ? (
          <img
            src={foto} alt={`Foto de ${atleta.nome}`}
            style={{
              width: lado, height: Math.round(lado * 1.25), objectFit: "cover",
              borderRadius: RAIO.sm, border: `1px solid ${BORDA}`, flexShrink: 0,
              filter: atleta.ativo ? "none" : "grayscale(1)",
            }}
          />
        ) : (
          <div style={{
            width: lado, height: Math.round(lado * 1.25), borderRadius: RAIO.sm,
            border: `1px dashed ${BORDA}`, background: FUNDO_SUAVE, flexShrink: 0,
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 6,
            filter: atleta.ativo ? "none" : "grayscale(1)",
          }}>
            <Avatar nome={atleta.nome} tamanho={44} />
            <span style={{ fontSize: 11, color: CINZA_ESC }}>sem foto</span>
          </div>
        )}

        <div style={{ flex: 1, minWidth: 220 }}>
          <LinhaDeCadastro rotulo="Nome" valor={atleta.nome} destaque />
          <LinhaDeCadastro rotulo="Núcleo" valor={nomeDoNucleo} />
          {/* O gênero vem do cartão da Gestão, que já o mostrava. Ele
              decide qual equação de composição corporal e de maturação
              roda na avaliação física, então vale aparecer onde se
              confere o cadastro. */}
          <LinhaDeCadastro rotulo="Gênero" valor={ROTULO_DO_GENERO[atleta.sexo] || ""} />
          <LinhaDeCadastro rotulo="Modalidade" valor={atleta.modalidade} />
          {/* "Principal" porque é a do cadastro, a da idade dele. A outra
              que ele disputa no mesmo evento é escolhida no lançamento do
              resultado, e aparece mais abaixo, entre as categorias
              disputadas. Sem a palavra, as duas pareciam a mesma coisa. */}
          <LinhaDeCadastro rotulo="Categoria principal" valor={atleta.categoria} />
          <LinhaDeCadastro
            rotulo="Data de Nascimento"
            valor={emBR(atleta.dataNascimento)}
            apoio={anos !== null ? `(${anos} anos)` : null}
          />
          <LinhaDeCadastro rotulo="E-mail" valor={atleta.email} />
          <LinhaDeCadastro
            rotulo="Situação"
            valor={atleta.ativo ? "Ativo no programa" : "Desligado"}
          />
        </div>

        <div style={{
          display: "flex", flexDirection: "column", gap: ESP.sm,
          alignItems: "flex-start", minWidth: 120,
        }}>
          <Etiqueta tom={atleta.ativo ? "ok" : "neutro"} ponto>
            {atleta.ativo ? "Ativo" : "Desligado"}
          </Etiqueta>
          <Etiqueta tom="info">{nomeDoNucleo}</Etiqueta>
        </div>
      </div>

      {/* O responsável só aparece para quem é menor, e na ficha do maior
          de idade o bloco inteiro some, com a linha divisória junto. Era
          um texto dizendo que ele assina o próprio termo, e isso é
          assunto de documento, não da pergunta que a ficha responde. */}
      {!maior && (
      <div style={{
        marginTop: ESP.xl, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}`,
      }}>
        {temResponsavel ? (
          <div style={{ display: "flex", gap: ESP.lg, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <LinhaDeCadastro
                rotulo="Responsável"
                valor={atleta.responsavelNome}
                apoio={atleta.responsavelParentesco || null}
              />
              <LinhaDeCadastro
                rotulo="Contato do responsável"
                valor={atleta.responsavelTelefone}
              />
            </div>
          </div>
        ) : (
          <StatusBox
            tipo="aviso"
            msg={
              atleta.dataNascimento
                ? "Sem os dados do responsável no cadastro. Eles são o que o termo de consentimento pede, e sem eles o termo não sai preenchido."
                : "Sem data de nascimento no cadastro. Enquanto ela faltar, o sistema trata o atleta como menor de idade e cobra os dados do responsável."
            }
          />
        )}
      </div>
      )}
    </Card>
  );
}

// ── Avaliação física ────────────────────────────────────────

// A última coleta em destaque e a série embaixo.
//
// A compilação é da série inteira, e não da coleta isolada, porque é a série
// que escolhe o método de maturação e a convenção do braço para todos os
// pontos de uma vez. Compilar ponto a ponto deixaria a primeira coleta num
// método e a última em outro, e o pico estimado andaria meses sem que nada
// tivesse acontecido com o atleta.
function BlocoAvaliacaoFisica({ atleta, coletas, isMobile, nucleo, nomeDoNucleo }) {
  const [imprimindo, setImprimindo] = useState(false);
  const [erroDoPDF, setErroDoPDF] = useState(null);
  const serie = useMemo(
    () => compilarSerie({
      sexo: atleta.sexo,
      dataNascimento: atleta.dataNascimento,
      avaliacoes: coletas,
    }),
    [atleta.sexo, atleta.dataNascimento, coletas],
  );

  if (!coletas.length) {
    return (
      <Card titulo="Avaliação física">
        <Vazio icone="—" titulo="Nenhuma coleta registrada.">
          Peso, estatura, dobras, perímetros e diâmetros são lançados na tela
          de Avaliação física. A partir deles a ficha mostra a composição
          corporal, o somatotipo e a maturação.
        </Vazio>
      </Card>
    );
  }

  const faltaSexo = serie.some((p) => p.faltaSexo) || !atleta.sexo;
  const faltaNascimento = !atleta.dataNascimento;
  const ultima = serie[serie.length - 1];
  const d = ultima.deurenberg;
  const s = ultima.somatotipo;
  const m = ultima.maturacao;
  const delta = ultima.delta;

  // A folha impressa é a mesma da tela de Avaliação física, gerada do
  // mesmo jeito e a partir da mesma série compilada. Duas folhas
  // diferentes para o mesmo atleta é o que ninguém saberia conferir.
  async function imprimir() {
    setImprimindo(true);
    setErroDoPDF(null);
    try {
      const daColeta = coletas.find((c) => c.data === ultima.data) || null;
      await gerarPDFAvaliacaoFisica({
        atleta, serie, nucleo, nucleoNome: nomeDoNucleo,
        sintese: (daColeta && daColeta.sintese) || null,
      });
    } catch (e) {
      setErroDoPDF(`Não consegui gerar o PDF: ${e.message}`);
    } finally {
      setImprimindo(false);
    }
  }

  return (
    <Card
      titulo="Avaliação física"
      sub={`Última coleta em ${emBR(ultima.data)}${serie.length > 1 ? ` · ${serie.length} coletas na série` : ""}`}
      acao={(
        <Btn variant="secondary" tamanho="sm" onClick={imprimir} disabled={imprimindo}>
          {imprimindo ? "Gerando…" : "Imprimir em PDF"}
        </Btn>
      )}
    >
      {erroDoPDF && <StatusBox tipo="erro" msg={erroDoPDF} />}
      {/* Cadastro incompleto trava a conta inteira, e a tela precisa dizer
          qual campo, não "não foi possível calcular". */}
      {(faltaSexo || faltaNascimento) && (
        <div style={{ marginBottom: ESP.lg }}>
          <StatusBox
            tipo="aviso"
            msg={
              `Falta ${[faltaSexo && "o sexo", faltaNascimento && "a data de nascimento"]
                .filter(Boolean).join(" e ")} no cadastro deste atleta. As equações de ` +
              "composição corporal e de maturação são específicas por sexo e por idade, " +
              "e sem esses campos elas não têm resultado."
            }
          />
        </div>
      )}

      <FileiraDeEstatisticas minimo={140}>
        <Estatistica
          rotulo="Peso" valor={`${num(ultima.peso, 1)} kg`} cor={AZUL}
          contexto={delta ? comSinal(delta.peso, "kg desde a anterior") : null}
        />
        <Estatistica
          rotulo="Estatura" valor={`${num(ultima.estatura, 1)} cm`} cor={AZUL}
          contexto={delta ? comSinal(delta.estatura, "cm desde a anterior") : null}
        />
        <Estatistica rotulo="IMC" valor={num(ultima.indices.imc, 1)} cor={AZUL} />
        <Estatistica
          rotulo="Gordura" valor={d && d.completo ? `${num(d.percentualGordura, 1)}%` : "—"}
          cor={AZUL}
          contexto={d && d.completo ? d.classificacao : dobrasQueFaltam(d)}
        />
      </FileiraDeEstatisticas>

      <div style={{ marginTop: ESP.lg }}>
        <GradeDeDados>
          <Dado
            rotulo="Somatotipo"
            valor={s && s.completo
              ? `${num(s.endomorfia, 1)} · ${num(s.mesomorfia, 1)} · ${num(s.ectomorfia, 1)}`
              : "—"}
            apoio={s && s.completo ? s.classificacao : "endo · meso · ecto"}
          />
          <Dado
            rotulo="Maturação"
            valor={m && m.completo ? m.estagio : "—"}
            apoio={m && m.completo
              ? `pico estimado aos ${num(m.idadeNoPico, 1)} anos (${m.metodo})`
              : null}
          />
          <Dado
            rotulo="Massa magra"
            valor={d && d.completo && d.massaMagra !== null ? `${num(d.massaMagra, 1)} kg` : "—"}
            apoio={delta ? comSinal(delta.massaMagra, "kg desde a anterior") : null}
          />
        </GradeDeDados>
      </div>

      {/* O gráfico antes da tabela: a ficha é para bater o olho, e a
          linha responde "está melhorando?" antes de alguém ler número.
          A tabela fica embaixo para quem quiser o valor exato.

          Uma medida por desenho, igual à avaliação física. Eram dois
          gráficos de duas linhas com um eixo de cada lado, e ali a
          proximidade entre as curvas era produzida pela escala, não pelos
          dados: gordura em % ao lado de massa magra em kg se cruzavam onde
          a escala mandasse. As duas telas mostram a MESMA série, e
          desenhá-la de dois jeitos fazia a ficha e a avaliação parecerem
          discordar quando diziam a mesma coisa. */}
      {serie.length > 1 && (
        <div style={{ marginTop: ESP.xl, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}` }}>
          <div style={{ ...TIPO.rotulo, color: CINZA_ESC, marginBottom: ESP.md }}>
            Evolução
          </div>
          <div style={{
            display: "grid", gap: ESP.lg,
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(260px, 1fr))",
          }}>
            {[
              INDICADORES.peso,
              INDICADORES.estatura,
              INDICADORES.imc,
              INDICADORES.gordura,
              INDICADORES.massaAdiposa,
              INDICADORES.massaMuscular,
            ].map(ind => (
              <GraficoDeMedida key={ind.id} serie={serie} indicador={ind} />
            ))}
          </div>
          <TrocaDeProtocolo serie={serie} />
        </div>
      )}
      {serie.length > 1 && <SerieDeColetas serie={serie} />}
    </Card>
  );
}

// Quantas dobras faltam para o protocolo fechar. Dizer "faltam 2 dobras" é
// o que leva alguém a voltar na coleta; "—" sozinho não leva a nada.
function dobrasQueFaltam(d) {
  if (!d || d.completo) return null;
  const n = (d.faltam || []).length;
  if (!n) return "não foi possível calcular";
  return `faltam ${n} ${n > 1 ? "medidas" : "medida"} do protocolo`;
}

function SerieDeColetas({ serie }) {
  return (
    <div style={{ marginTop: ESP.xl, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}` }}>
      <div style={{ ...TIPO.rotulo, color: CINZA_ESC, marginBottom: ESP.md }}>
        Série do atleta
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
          <thead>
            <tr>
              {["Coleta", "Peso", "Estatura", "Gordura", "Crescimento"].map((h) => (
                <th key={h} style={{
                  ...TIPO.rotulo, color: CINZA_ESC, textAlign: h === "Coleta" ? "left" : "right",
                  padding: "0 0 8px", whiteSpace: "nowrap",
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...serie].reverse().map((p) => (
              <tr key={p.data} style={{ borderTop: `1px solid ${BORDA}` }}>
                <td style={{ ...TIPO.apoio, color: TEXTO, padding: "9px 0", whiteSpace: "nowrap" }}>
                  {emBR(p.data)}
                  {p.origem === "isak" && (
                    <span style={{ marginLeft: 6 }}><Etiqueta tom="neutro">ISAK</Etiqueta></span>
                  )}
                </td>
                <td style={celulaNumero}>{num(p.peso, 1)}</td>
                <td style={celulaNumero}>{num(p.estatura, 1)}</td>
                <td style={celulaNumero}>
                  {p.deurenberg && p.deurenberg.completo ? `${num(p.deurenberg.percentualGordura, 1)}%` : "—"}
                </td>
                {/* A velocidade de crescimento só existe entre dois pontos, e
                    só vale quando o intervalo é longo o bastante: dois meses
                    entre coletas transformam meio centímetro de erro de
                    medida numa velocidade de 3 cm/ano que nunca existiu. */}
                <td style={celulaNumero}>
                  {!p.delta ? "—"
                    : p.delta.estaturaRegrediu ? "medida a conferir"
                    : p.delta.intervaloCurto ? "intervalo curto"
                    : p.delta.velocidadeDeCrescimento !== null
                      ? `${num(p.delta.velocidadeDeCrescimento, 1)} cm/ano`
                      : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const celulaNumero = {
  ...TIPO.apoio, color: TEXTO, padding: "9px 0", textAlign: "right",
  whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
};

// ── Testes físicos ──────────────────────────────────────────

// A bateria de testes já tem cálculo pronto (testesFisicos.js: direção de
// cada teste, marcas de ausência, evolução entre coletas), mas ainda não
// tem lançamento: não existe coleção no Firestore e a tela de Testes
// físicos está como "Em breve".
//
// O bloco aparece mesmo assim, dizendo isso. A alternativa seria omiti-lo,
// e quem abre a ficha procurando o salto do atleta concluiria que ele não
// foi medido, quando o que falta é a tela de lançar.
// ── Avaliação técnica ───────────────────────────────────────
//
// A leitura, e não o lançamento: quem avalia abre Desempenho, aba
// Avaliação técnica. Aqui aparece o último estado do atleta e a série
// resumida, do mesmo jeito que a avaliação física aparece acima.
function BlocoTecnica({ atleta, avaliacoes, nucleo, nomeDoNucleo }) {
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState(null);

  const serie = useMemo(
    () => compilarSerieTecnica({ modalidade: atleta.modalidade, avaliacoes }),
    [atleta.modalidade, avaliacoes],
  );
  const ponto = serie[serie.length - 1] || null;
  const laudo = ponto ? laudoTecnico(ponto) : null;

  async function imprimir() {
    setGerando(true);
    setErro(null);
    try {
      await gerarPDFAvaliacaoTecnica({
        atleta, serie, escores: [], nucleo, nucleoNome: nomeDoNucleo,
      });
    } catch (e) {
      setErro(`Não consegui gerar o PDF: ${e.message}`);
    } finally {
      setGerando(false);
    }
  }

  if (!ponto) {
    return (
      <Card titulo="Avaliação técnica">
        <Vazio icone="—" titulo="Ainda não há avaliação técnica lançada para este atleta.">
          Os critérios do esporte, de 1 a 10, são lançados em Desempenho, aba Avaliação
          técnica. Assim que a primeira entrar, a nota de cada critério aparece aqui.
        </Vazio>
      </Card>
    );
  }

  return (
    <Card
      titulo="Avaliação técnica"
      acao={(
        <Btn variant="secondary" tamanho="sm" onClick={imprimir} disabled={gerando}>
          {gerando ? "Gerando…" : "Imprimir"}
        </Btn>
      )}
    >
      {erro && <StatusBox tipo="erro" msg={erro} />}
      <FileiraDeEstatisticas>
        <Estatistica rotulo="Média dos critérios" valor={umaCasa(ponto.media, 2)} />
        <Estatistica rotulo="Faixa" valor={laudo ? laudo.classificacao : "—"} />
        <Estatistica rotulo="Avaliações" valor={serie.length} />
        <Estatistica rotulo="Última" valor={emBRFicha(ponto.data)} />
      </FileiraDeEstatisticas>

      <div style={{
        display: "grid", gap: 8, marginTop: ESP.lg,
        gridTemplateColumns: "repeat(auto-fill, minmax(165px, 1fr))",
      }}>
        {ponto.criterios.map((c) => (
          <div key={c.id} style={{
            border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "9px 11px",
          }}>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>{c.rotulo}</div>
            <div style={{ ...TIPO.corpo, fontWeight: 700, color: TEXTO }}>
              {umaCasa(c.nota, 1)}
              {c.deTempo && Number.isFinite(c.tempo) && (
                <span style={{ ...TIPO.apoio, fontWeight: 400, color: CINZA_ESC }}>
                  {` · ${umaCasa(c.tempo, 2)} s`}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {laudo && (
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6, marginTop: ESP.lg }}>
          {laudo.texto}
        </div>
      )}
    </Card>
  );
}

// ── Testes físicos ──────────────────────────────────────────

function BlocoTestes({ atleta, coletas, nucleo, nomeDoNucleo }) {
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState(null);

  const serie = useMemo(
    () => compilarSerieDeTestes({
      modalidade: atleta.modalidade,
      idadeEm: (data) => idadeDecimalEm(atleta.dataNascimento, data),
      coletas,
    }),
    [atleta.modalidade, atleta.dataNascimento, coletas],
  );
  const ponto = serie[serie.length - 1] || null;

  async function imprimir() {
    setGerando(true);
    setErro(null);
    try {
      await gerarPDFTestesFisicos({
        atleta, serie, escores: [], nucleo, nucleoNome: nomeDoNucleo,
      });
    } catch (e) {
      setErro(`Não consegui gerar o PDF: ${e.message}`);
    } finally {
      setGerando(false);
    }
  }

  if (!ponto) {
    return (
      <Card titulo="Testes físicos">
        {/* Os testes DESTE atleta, que são os da modalidade dele. A
            frase antiga listava os das três de uma vez, e a ficha de um
            skatista falava do tempo de pista do BMX. */}
        <Vazio icone="—" titulo="Ainda não há testes lançados para este atleta.">
          {bateriaDa(atleta.modalidade).map(t => t.rotulo).join(", ") || "Os testes da modalidade"}
          {" são lançados em Desempenho, aba Testes físicos, junto das quatro capacidades "}
          {"por nota. Assim que a primeira coleta entrar, a série de cada teste aparece aqui."}
        </Vazio>
      </Card>
    );
  }

  return (
    <Card
      titulo="Testes físicos"
      acao={(
        <Btn variant="secondary" tamanho="sm" onClick={imprimir} disabled={gerando}>
          {gerando ? "Gerando…" : "Imprimir"}
        </Btn>
      )}
    >
      {erro && <StatusBox tipo="erro" msg={erro} />}
      <FileiraDeEstatisticas>
        <Estatistica rotulo="Média das capacidades" valor={umaCasa(ponto.media, 2)} />
        <Estatistica
          rotulo="Testes com resultado"
          valor={`${ponto.testes.filter((t) => t.valor !== null).length} de ${ponto.testes.length}`}
        />
        <Estatistica rotulo="Coletas" valor={serie.length} />
        <Estatistica rotulo="Última" valor={emBRFicha(ponto.data)} />
      </FileiraDeEstatisticas>

      <div style={{
        display: "grid", gap: 8, marginTop: ESP.lg,
        gridTemplateColumns: "repeat(auto-fill, minmax(165px, 1fr))",
      }}>
        {ponto.testes.map((t) => (
          <div key={t.id} style={{
            border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "9px 11px",
          }}>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>{t.rotulo}</div>
            <div style={{ ...TIPO.corpo, fontWeight: 700, color: TEXTO }}>
              {t.valor !== null ? `${umaCasa(t.valor, t.unidade === "s" ? 2 : 1)} ${t.unidade}` : "—"}
            </div>
            {Number.isFinite(t.evolucao) && (
              <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                {`${t.evolucao > 0 ? "+" : t.evolucao < 0 ? "−" : ""}`}
                {`${umaCasa(Math.abs(t.evolucao), 1)}% desde ${emBRFicha(t.compararamCom)}`}
              </div>
            )}
            {/* O que o avaliador viu dentro do teste, quando o teste rende
                mais que o número do cronômetro. Fora do escore global. */}
            {(t.criterios || []).filter((c) => Number.isFinite(c.nota)).map((c) => (
              <div key={c.id} style={{
                ...TIPO.apoio, display: "flex", justifyContent: "space-between", gap: 6,
                color: CINZA_ESC, marginTop: 2,
              }}>
                <span>{c.rotulo}</span>
                <span style={{ color: TEXTO, fontWeight: 600 }}>{umaCasa(c.nota, 1)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── O escore global ─────────────────────────────────────────
//
// O quadro que junta as duas metades, com a conta ao lado. Fica depois
// dos dois blocos porque é a soma deles, e some quando o atleta não tem
// as duas avaliações na mesma onda: meio escore não é escore.
function BlocoEscore({ escores }) {
  const comGlobal = escores.filter((e) => Number.isFinite(e.global));
  if (!comGlobal.length) return null;
  const ultimo = comGlobal[comGlobal.length - 1];
  return (
    <Card titulo="Escore global de desempenho">
      <EscoreGlobal escore={ultimo} />
      <LegendaDosNiveis compacta />
    </Card>
  );
}

function umaCasa(v, casas = 1) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(casas).replace(".", ",") : "—";
}

function emBRFicha(iso) {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return d ? `${d}/${m}/${a}` : iso;
}

// ── Competições ─────────────────────────────────────────────

function BlocoCompeticoes({ atleta, nucleo }) {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [ranking, setRanking] = useState(null);

  useEffect(() => {
    let vivo = true;
    if (!atleta.modalidade) { setCarregando(false); return undefined; }
    setCarregando(true); setErro(null); setRanking(null);
    rankingDeMedalhas({ nucleo, modalidade: atleta.modalidade })
      .then((r) => { if (vivo) setRanking(r); })
      .catch((e) => { if (vivo) setErro(e.message || "Não consegui carregar as competições."); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [nucleo, atleta.modalidade]);

  if (!atleta.modalidade) {
    return (
      <Card titulo="Desempenho em competições">
        <StatusBox
          tipo="aviso"
          msg="Sem modalidade no cadastro deste atleta. As competições são organizadas por modalidade, então não há de onde puxar o histórico dele."
        />
      </Card>
    );
  }
  if (carregando) return <Card titulo="Desempenho em competições"><Carregando linhas={4} /></Card>;
  if (erro) return <Card titulo="Desempenho em competições"><StatusBox tipo="erro" msg={erro} /></Card>;

  const linha = (ranking.linhas || []).find((l) => l.uid === atleta.uid);
  const tamanhoDoPodio = ranking.tamanhoDoPodio || 3;

  if (!linha) {
    return (
      <Card titulo="Desempenho em competições">
        <Vazio icone="—" titulo="Nenhum resultado lançado para este atleta.">
          As competições de {atleta.modalidade} estão em Planejamento ›
          Competições e resultados. Enquanto a colocação não for lançada, não
          há participação para contar.
        </Vazio>
      </Card>
    );
  }

  // O local fica na lista de competições da resposta, não no lançamento do
  // atleta. Sem este cruzamento a linha do tempo mostraria só a data, e
  // "Copa Brasil Ouro" aconteceu em Caruaru e em Teresina no mesmo ano.
  const locais = {};
  (ranking.competicoes || []).forEach((c) => { locais[c.id] = c.local || null; });
  const eventos = agruparPorEvento(linha.lancamentos)
    .map((e) => ({ ...e, local: e.local || locais[e.id] || null }));
  const melhor = melhorDoAno(linha.lancamentos);
  const principal = linha.principal || {};

  return (
    <Card
      titulo="Desempenho em competições"
      sub={`Pódio de ${atleta.modalidade}: até o ${tamanhoDoPodio}º lugar.`}
    >
      {/* Contagem primeiro: participação e pódio são fatos. Uma
          participação é uma CATEGORIA disputada, não um evento: o mesmo
          campeonato rende cinco no tênis de mesa, e é em cada uma delas que
          existe a chance de pódio. */}
      <FileiraDeEstatisticas minimo={140}>
        <Estatistica
          rotulo="Participações" valor={linha.participacoes} cor={AZUL}
          contexto="categorias disputadas"
        />
        <Estatistica rotulo="Pódios" valor={linha.totalMedalhas} cor={AZUL} />
        <Estatistica
          rotulo="Competições" valor={linha.competicoes} cor={AZUL}
          contexto="eventos distintos"
        />
        <Estatistica
          rotulo="Melhor resultado" valor={melhor ? `${melhor.posicao}º` : "—"} cor={AZUL}
          contexto={melhor
            ? [melhor.categoria, melhor.competicao].filter(Boolean).join(" · ")
            : "nenhuma colocação numérica registrada"}
        />
      </FileiraDeEstatisticas>

      <QuadroDeMedalhas medalhas={linha.medalhas} porColocacao={linha.porColocacao}
        tamanhoDoPodio={tamanhoDoPodio} />

      <RankingDaFederacao atleta={atleta} />

      {/* O percentual entra pequeno e rotulado pelo que ele é. Grande, ao
          lado da foto, ele viraria nota do atleta, e quem disputa muita
          categoria acima do nível dele sai pior no número sem ter ido pior
          na competição. Por isso o recorte da categoria da idade vem ao
          lado: é o que responde "como ele vai no nível dele". */}
      <div style={{
        display: "flex", gap: ESP.xl, flexWrap: "wrap",
        marginTop: ESP.lg, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}`,
      }}>
        <div>
          <div style={{ ...TIPO.rotulo, color: CINZA_ESC }}>Pódios em participações</div>
          <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700, marginTop: 3 }}>
            {pct(linha.aproveitamento)}
            <span style={{ fontWeight: 400, color: CINZA_ESC }}>
              {" "}({linha.totalMedalhas} em {linha.participacoes})
            </span>
          </div>
        </div>
        <div>
          <div style={{ ...TIPO.rotulo, color: CINZA_ESC }}>Na categoria principal</div>
          <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700, marginTop: 3 }}>
            {pct(principal.aproveitamento)}
            <span style={{ fontWeight: 400, color: CINZA_ESC }}>
              {" "}({principal.podios || 0} em {principal.participacoes || 0})
            </span>
          </div>
          <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>a categoria da idade</div>
        </div>
      </div>

      <LinhaDoTempo eventos={eventos} tamanhoDoPodio={tamanhoDoPodio} />
    </Card>
  );
}

// O ranking da federação, que é de outra fonte que todo o resto do bloco.
//
// Ele NÃO sai dos resultados lançados aqui, e não poderia: a federação
// ranqueia o estado inteiro, e o programa só enxerga os próprios atletas.
// Por isso é digitado no cadastro, e por isso aparece separado das
// contagens, com a data em que foi consultado. Um 2º estadual de março
// lido em outubro parece a posição de hoje, e não é.
function RankingDaFederacao({ atleta }) {
  const historico = historicoComEvolucao(historicoDoAtleta(atleta));

  // Cadastro antigo, com posição mas sem a data da consulta.
  //
  // Sem data não há ano, e sem ano a posição não entra no histórico, que é
  // organizado por temporada. Mas a posição existe e foi apurada: escondê-la
  // seria apagar dado por causa do formato. Ela aparece como aparecia antes,
  // com a ressalva de que não se sabe de quando é.
  if (!historico.length) {
    const estadual = rotuloDoRanking(atleta.rankingEstadual);
    const nacional = rotuloDoRanking(atleta.rankingNacional);
    if (!estadual && !nacional) return null;
    return (
      <div style={{
        display: "flex", gap: ESP.xl, flexWrap: "wrap", alignItems: "baseline",
        marginTop: ESP.lg, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}`,
      }}>
        <div>
          <div style={{ ...TIPO.rotulo, color: CINZA_ESC }}>Ranking estadual</div>
          <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700, marginTop: 3 }}>
            {estadual || "—"}
          </div>
        </div>
        <div>
          <div style={{ ...TIPO.rotulo, color: CINZA_ESC }}>Ranking nacional</div>
          <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700, marginTop: 3 }}>
            {nacional || "—"}
          </div>
        </div>
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, maxWidth: 360 }}>
          Sem data de consulta registrada, então esta posição não entra no
          comparativo por ano.
        </div>
      </div>
    );
  }

  const ultimo = historico[historico.length - 1];

  return (
    <div style={{ marginTop: ESP.lg, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}` }}>
      <div style={{ ...TIPO.rotulo, color: CINZA_ESC, marginBottom: ESP.sm }}>
        Ranking da federação
      </div>

      {/* A posição mais recente em destaque, porque é a pergunta de quem
          abre a ficha. O histórico vem abaixo, porque a segunda pergunta
          é sempre "e antes?". */}
      <div style={{ display: "flex", gap: ESP.xl, flexWrap: "wrap", alignItems: "baseline" }}>
        <div>
          <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>Estadual em {ultimo.ano}</div>
          <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700, marginTop: 3 }}>
            {rotuloDoRanking(ultimo.estadual) || "—"}
          </div>
        </div>
        <div>
          <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>Nacional em {ultimo.ano}</div>
          <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700, marginTop: 3 }}>
            {rotuloDoRanking(ultimo.nacional) || "—"}
          </div>
        </div>
      </div>

      {/* Uma linha por ano, com a evolução ao lado.
          O percentual vem acompanhado das posições porque "+60%" sozinho
          não diz a ninguém que o atleta saiu de 5º para 2º. E a evolução
          aqui é a única do sistema em que o positivo nasce de um número
          que caiu: posição menor é melhor. */}
      {historico.length > 1 && (
        <div style={{ overflowX: "auto", marginTop: ESP.md }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                {["Ano", "Estadual", "Evolução", "Nacional", "Evolução"].map((t, i) => (
                  <th key={t + i} style={{
                    ...TIPO.apoio, color: CINZA_ESC, fontWeight: 800, textTransform: "uppercase",
                    textAlign: i === 0 ? "left" : "center", padding: "6px 8px",
                    borderBottom: `1px solid ${BORDA}`, whiteSpace: "nowrap",
                  }}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...historico].reverse().map((linha) => (
                <tr key={linha.ano} style={{ borderBottom: `1px solid ${FUNDO_SUAVE}` }}>
                  <td style={{ ...TIPO.corpo, color: TEXTO, padding: "6px 8px", fontWeight: 600 }}>
                    {linha.ano}
                  </td>
                  <td style={{ ...TIPO.corpo, color: TEXTO, padding: "6px 8px", textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                    {rotuloDoRanking(linha.estadual) || "—"}
                  </td>
                  <CelulaDeEvolucao evolucao={linha.evolucaoEstadual} />
                  <td style={{ ...TIPO.corpo, color: TEXTO, padding: "6px 8px", textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                    {rotuloDoRanking(linha.nacional) || "—"}
                  </td>
                  <CelulaDeEvolucao evolucao={linha.evolucaoNacional} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: ESP.sm, lineHeight: 1.55 }}>
        {atleta.rankingAtualizadoEm
          ? `Consultada em ${emBR(atleta.rankingAtualizadoEm)}. `
          : "Sem data de consulta registrada. "}
        Posição menor é melhor, então subir no ranking deixa o percentual positivo.
        Ano sem consulta não entra na comparação: o ano seguinte compara com o último
        que tiver posição.
      </div>
    </div>
  );
}

function CelulaDeEvolucao({ evolucao }) {
  const cor = !evolucao || evolucao.posicoes === 0
    ? CINZA_ESC
    : (evolucao.posicoes > 0 ? OK : PERIGO);
  return (
    <td title={rotuloDasPosicoes(evolucao)} style={{
      ...TIPO.apoio, color: cor, fontWeight: 800, padding: "6px 8px",
      textAlign: "center", whiteSpace: "nowrap",
    }}>
      {rotuloDaEvolucao(evolucao) || "—"}
    </td>
  );
}

// Pódio e medalha são a mesma coisa: quem sobe, recebe. Nas modalidades de
// pódio maior que três, do quarto em diante a medalha não tem metal, e a
// coluna se chama "Medalha" em vez de inventar um nome.
// Uma pílula por COLOCAÇÃO premiada, como no quadro da modalidade e no
// relatório em PDF.
//
// O pódio é cadastro da modalidade, e onde ele vai até o 5º, como no
// BMX, o 4º e o 5º são pódio com a mesma dignidade do 3º. Até 15/10/2026
// os dois apareciam somados numa pílula "Medalha", que os fazia parecer
// sobra.
//
// Lançamento antigo, sem a contagem posição a posição, volta aos três
// metais: é o que aqueles dados sabem dizer.
const SINAL = { 1: "🥇", 2: "🥈", 3: "🥉" };
const METAL = { 1: "Ouro", 2: "Prata", 3: "Bronze" };

function QuadroDeMedalhas({ medalhas, porColocacao, tamanhoDoPodio }) {
  const m = medalhas || {};
  let itens;
  if (porColocacao) {
    const ate = Math.max(1, Number(tamanhoDoPodio) || 3);
    itens = [];
    for (let i = 1; i <= ate; i += 1) {
      itens.push({
        sinal: SINAL[i] || "🏅",
        rotulo: METAL[i] || `${i}º lugar`,
        valor: porColocacao[i] || 0,
      });
    }
  } else {
    itens = [
      { sinal: "🥇", rotulo: "Ouro", valor: m.ouro || 0 },
      { sinal: "🥈", rotulo: "Prata", valor: m.prata || 0 },
      { sinal: "🥉", rotulo: "Bronze", valor: m.bronze || 0 },
    ];
    if (tamanhoDoPodio > 3 || m.outras) {
      itens.push({ sinal: "🏅", rotulo: "Medalha", valor: m.outras || 0 });
    }
  }

  return (
    <div style={{ display: "flex", gap: ESP.sm, flexWrap: "wrap", marginTop: ESP.md }}>
      {itens.map((i) => (
        <div key={i.rotulo} style={{
          display: "flex", alignItems: "center", gap: 7,
          background: FUNDO_SUAVE, border: `1px solid ${BORDA}`,
          borderRadius: RAIO.pilula, padding: "6px 13px",
        }}>
          <span style={{ fontSize: 15 }}>{i.sinal}</span>
          <span style={{ ...TIPO.corpo, fontWeight: 700, color: TEXTO, fontVariantNumeric: "tabular-nums" }}>
            {i.valor}
          </span>
          <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>{i.rotulo}</span>
        </div>
      ))}
    </div>
  );
}

// A linha do tempo: um bloco por evento, com as categorias dentro.
//
// Agrupada porque uma linha de lançamento é uma categoria, não um evento:
// cinco categorias num fim de semana são uma competição, e listá-las soltas
// faria parecer cinco.
function LinhaDoTempo({ eventos, tamanhoDoPodio }) {
  if (!eventos.length) return null;

  return (
    <div style={{ marginTop: ESP.xl, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}` }}>
      <div style={{ ...TIPO.rotulo, color: CINZA_ESC, marginBottom: ESP.md }}>
        Competições disputadas
      </div>

      <div style={{ display: "grid", gap: ESP.md }}>
        {eventos.map((e) => {
          const med = e.melhor ? medalhaDe(e.melhor.colocacao, tamanhoDoPodio) : null;
          return (
            <div key={e.id} style={{
              border: `1px solid ${BORDA}`, borderRadius: RAIO.md, overflow: "hidden",
            }}>
              <div style={{
                display: "flex", alignItems: "baseline", justifyContent: "space-between",
                gap: ESP.md, flexWrap: "wrap",
                background: FUNDO_SUAVE, padding: "10px 14px",
                borderBottom: `1px solid ${BORDA}`,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ ...TIPO.corpo, fontWeight: 700, color: TEXTO }}>{e.nome}</div>
                  <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                    {[emBR(e.data), e.local].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {/* O melhor resultado do evento encabeça o bloco: é o que se
                    procura ao correr o olho pela linha do tempo. */}
                {e.melhor && (
                  <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {med && <span style={{ marginRight: 5 }}>{med.sinal}</span>}
                    {rotuloDaColocacao(e.melhor.colocacao)}
                    {e.provas.length > 1 && (
                      <span style={{ fontWeight: 400, color: CINZA_ESC }}>
                        {" "}· {e.provas.length} categorias
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div style={{ padding: "4px 14px 8px" }}>
                {e.provas.map((p, i) => {
                  const medal = medalhaDe(p.colocacao, tamanhoDoPodio);
                  return (
                    <div
                      key={`${p.categoria || ""}-${i}`}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: ESP.md, padding: "7px 0",
                        borderTop: i === 0 ? "none" : `1px solid ${BORDA}`,
                      }}
                    >
                      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: ESP.sm, flexWrap: "wrap" }}>
                        <span style={{ ...TIPO.apoio, color: TEXTO }}>
                          {p.categoria || "categoria não informada"}
                        </span>
                        {/* Principal é a categoria da idade do atleta;
                            secundária é idade acima, absoluto ou dupla. Sem a
                            distinção, quem aceita o desafio de subir de
                            categoria aparece pior na ficha do que quem só
                            disputa o nível dele. */}
                        {p.tipo === "principal" && <Etiqueta tom="info">Principal</Etiqueta>}
                        {p.tipo === "secundaria" && <Etiqueta tom="neutro">Secundária</Etiqueta>}
                        {p.prova && (
                          <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>{p.prova}</span>
                        )}
                      </div>
                      <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {medal && <span style={{ marginRight: 5 }}>{medal.sinal}</span>}
                        {rotuloDaColocacao(p.colocacao)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
